import React, { useEffect, useState } from "react";
import { api } from "@/api/client";
import { School, Bus, Pill, ShoppingCart, UtensilsCrossed, Trees, Stethoscope, Compass, ChevronDown } from "lucide-react";

const CATEGORIES = [
  { key: "schools",      label: "Escolas",       Icon: School },
  { key: "transport",    label: "Transportes",   Icon: Bus },
  { key: "pharmacies",   label: "Farmácias",     Icon: Pill },
  { key: "supermarkets", label: "Supermercados", Icon: ShoppingCart },
  { key: "restaurants",  label: "Restauração",   Icon: UtensilsCrossed },
  { key: "parks",        label: "Parques",       Icon: Trees },
  { key: "health",       label: "Saúde",         Icon: Stethoscope },
];

function fmtDist(m) {
  if (m == null) return "";
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function CategoryCard({ label, Icon, data, testId }) {
  const [open, setOpen] = useState(false);
  if (!data || data.count === 0) return null;
  const hasExamples = data.examples && data.examples.length > 0;

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => hasExamples && setOpen((v) => !v)}
      aria-expanded={open}
      aria-label={`${label}: ${data.count} num raio. ${open ? "Fechar" : "Ver detalhes"}`}
      className={`card-soft p-5 text-left transition-all duration-200 ${hasExamples ? "hover:border-[#103C4A]/30 hover:shadow-[0_10px_30px_rgba(16,60,74,0.08)] cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-start justify-between">
        <Icon className="h-4 w-4 text-[#103C4A]" />
        {hasExamples && (
          <ChevronDown
            className={`h-4 w-4 text-stone-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        )}
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="font-display text-3xl text-stone-900">{data.count}</div>
        {data.nearest_m != null && (
          <div className="text-xs text-stone-500">a partir de {fmtDist(data.nearest_m)}</div>
        )}
      </div>

      {hasExamples && !open && (
        <div className="mt-2 text-xs text-stone-500 leading-relaxed line-clamp-2">
          {data.examples.slice(0, 3).map((ex) => ex.name).join(" · ")}
        </div>
      )}

      {hasExamples && open && (
        <ul className="mt-3 space-y-1.5 text-xs" data-testid={`${testId}-expanded`}>
          {data.examples.map((ex, i) => (
            <li key={`${ex.name}-${i}`} className="flex items-baseline justify-between gap-3">
              <span className="text-stone-700 truncate">{ex.name}</span>
              <span className="text-stone-400 tabular-nums shrink-0">{fmtDist(ex.dist_m)}</span>
            </li>
          ))}
        </ul>
      )}
    </button>
  );
}

export default function NeighborhoodInsights({ lat, lon, radius = 1000 }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    if (lat == null || lon == null) {
      setState({ loading: false, data: null, error: null });
      return () => { alive = false; };
    }
    setState({ loading: true, data: null, error: null });
    api
      .get("/neighborhood", { params: { lat, lon, radius } })
      .then((r) => { if (alive) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => { if (alive) setState({ loading: false, data: null, error: e }); });
    return () => { alive = false; };
  }, [lat, lon, radius]);

  if (!lat || !lon || state.error) return null;

  const hasAny =
    state.data &&
    Object.values(state.data.categories).some((c) => c.count > 0);

  if (!state.loading && !hasAny) return null;

  return (
    <div className="mt-10" data-testid="neighborhood-insights">
      <div className="flex items-baseline justify-between gap-6 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-stone-500">O bairro</div>
          <h3 className="mt-1 font-display text-2xl md:text-3xl text-stone-900 tracking-[-0.02em]">
            O que existe à volta
          </h3>
        </div>
        <div className="hidden md:flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-stone-400">
          <Compass className="h-3 w-3" /> num raio de {radius} m
        </div>
      </div>
      <div className="rule-hair mb-5" />

      {state.loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CATEGORIES.slice(0, 4).map((c) => (
            <div key={c.key} className="card-soft p-5 animate-pulse">
              <div className="h-4 w-4 rounded-full bg-stone-200 mb-4" />
              <div className="h-6 w-14 bg-stone-200 rounded mb-2" />
              <div className="h-3 w-24 bg-stone-100 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {CATEGORIES.map(({ key, label, Icon }) => (
            <CategoryCard
              key={key}
              label={label}
              Icon={Icon}
              data={state.data?.categories?.[key]}
              testId={`neigh-cat-${key}`}
            />
          ))}
        </div>
      )}

      <p className="mt-4 text-[11px] text-stone-400">
        Fonte: OpenStreetMap. Clique num card para ver os locais próximos.
      </p>
    </div>
  );
}
